import { render } from 'preact';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import { initThemeListener, loadRuntimeConfig } from '@xantory/store';
// Self-hosted webfonts (R-300). Before reset.css so the @font-face rules are
// registered when the first styled node paints. NOT a CDN: the farm site has no
// internet — see packages/ui/src/fonts/fonts.css.
import '@xantory/ui/fonts.css';
import './reset.css';

initThemeListener();

// Reload the tab, unless it already reloaded for the same reason within the last minute — a
// genuinely broken deploy must not reload forever. A timestamp, not a once-per-tab flag: the flag
// was never cleared, so in a long-lived tab the NEXT deploy got a blank screen instead of a reload.
function reloadUnlessJustReloaded(key: string): boolean {
  try {
    if (Date.now() - Number(sessionStorage.getItem(key)) < 60_000) return false;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // Storage blocked: reload anyway — one blank screen is worse than a possible second reload.
  }
  window.location.reload();
  return true;
}

// A deploy replaces every content-hashed chunk. A tab still running the previous build asks
// for one of its old chunks on the next navigation — the server no longer has it, and the new
// service worker's precache never did — so Vite raises `vite:preloadError` and the screen goes
// blank until someone hard-refreshes. Seen on production twice on 2026-09-21, once per console
// deploy. Reload so the tab picks up the new shell.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadUnlessJustReloaded('xantory.reloaded-for-preload-error')) event.preventDefault();
});

// R-093 4.3 / R-133: PWA service worker (installable + offline app shell). Registered
// manually so long-lived pages (wall-mounted farm consoles nobody reloads) poll for a
// new deploy every 60s — `registerType: 'autoUpdate'` then swaps the bundle in place,
// ending the stale-after-redeploy trap that needed a hard reload.
let swRegistration: ServiceWorkerRegistration | undefined;
registerSW({
  onRegisteredSW(_url, registration) {
    swRegistration = registration;
    if (registration) setInterval(() => void registration.update(), 60_000);
  },
});

// New-deploy detection that does not depend on the service worker. The site console is plain
// HTTP on an IP — not a secure context — so no SW runs there and, before this, an open tab kept
// running the old bundle against a newer backend until something broke. /version.json carries
// the deployed build's id (vite.config.ts); when it differs from the one this tab was built with:
//   - SW in control → ask it to update; `autoUpdate` reloads the page once the new one activates.
//     A plain reload here would only be answered by the OLD worker's precached shell.
//   - no SW         → reload; Caddy serves the shell with `no-cache`, so the reload is fresh.
const BUILD_ID = import.meta.env['VITE_BUILD_ID'] as string | undefined;
async function checkForNewDeploy(): Promise<void> {
  let deployed: unknown;
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return;
    deployed = ((await res.json()) as { id?: unknown }).id;
  } catch {
    // Offline, or a server still on a build without version.json (answered with the HTML shell).
    return;
  }
  if (typeof deployed !== 'string' || deployed === BUILD_ID) return;
  if (navigator.serviceWorker?.controller && swRegistration) void swRegistration.update();
  else reloadUnlessJustReloaded('xantory.reloaded-for-new-deploy');
}
if (BUILD_ID) {
  setInterval(() => void checkForNewDeploy(), 60_000);
  // Timers are throttled in background tabs; check the moment the operator comes back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForNewDeploy();
  });
  window.addEventListener('online', () => void checkForNewDeploy());
}

// Resolve the deployment mode (config.json, or VITE_DEPLOY_MODE in dev) BEFORE first render, so the
// URL resolver and mode-gated UI see the correct mode from the very first paint.
const devFallback = (import.meta.env['VITE_DEPLOY_MODE'] as 'local' | 'cloud' | undefined) ?? 'local';
loadRuntimeConfig(devFallback).then(() => {
  render(<App />, document.getElementById('app')!);
});
